import { SessionHandler } from "verdi-web-client";
import { ApiError } from "./api-error";

// Matches UTC (`Z`) and offset (`+05:00`) datetime strings
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

function convertDateStringToDate(_key: string, value: unknown): unknown {
    if (typeof value === "string" && ISO_DATE_RE.test(value)) {
        return new Date(value);
    }
    return value;
}

// Dynamic access to avoid esbuild CJS warning during Orval generation
// At runtime, Vite properly provides import.meta.env
const getEnv = () => import.meta.env;

/**
 * Reads the session token, reporting a failed refresh the same way the server's own rejection is reported:
 * as a 401.
 *
 * Left as the plain Error SessionHandler throws, React Query's retry predicate cannot tell it from a
 * transient fault and retries it — and every retry re-enters Descope refresh, which rotates the refresh
 * token. A dead session is an auth outcome, so it should fail once.
 *
 * Deliberately catching a refresh that failed on the network too, rather than letting that one stay
 * retryable: a refresh whose response was lost may already have rotated the token server-side, which is
 * exactly the case where retrying turns a recoverable session into a dead one.
 */
async function getSessionTokenOrThrowUnauthorized(): Promise<string | undefined> {
    try {
        return await SessionHandler.getValidSessionToken();
    } catch (error) {
        throw new ApiError(401, { message: error instanceof Error ? error.message : "Session refresh failed." });
    }
}

/**
 * Custom fetch mutator for Orval-generated API clients.
 *
 * Returns the parsed body rather than Orval's `{ data, status, headers }` envelope, which is what
 * `includeHttpResponseReturnType: false` in orval-config declares. The `as T` casts below are
 * unchecked, so changing one side without the other breaks every call site silently.
 *
 * Throws `ApiError` on non-2xx responses so React Query enters its error state.
 *
 * Token handling goes through SessionHandler so this client and the legacy `doFetch` share one refresh
 * latch and one 401 counter — two of either would race.
 */
export async function customFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
    // Derive v2 base from the shared backend URL
    const baseURLV2 = `${(getEnv().VITE_BACKEND_URL ?? "").replace(/\/+$/, "")}/v2`;
    const path = url.startsWith("/") ? url : `/${url}`;
    const fullURL = `${baseURLV2}${path}`;

    const sessionToken = await getSessionTokenOrThrowUnauthorized();

    // The User header lets admin users specify which account they're acting as
    const headers = new Headers(options.headers ?? {});

    if (sessionToken) {
        headers.set("Authorization", `Bearer ${sessionToken}`);
    }

    if (SessionHandler.curUser) {
        headers.set("User", SessionHandler.curUser);
    }

    // Express gzip is opt-in. Poll and session seed both go through this mutator, so this has to live here rather than on a single call site.
    headers.set("compress", "true");

    const res = await fetch(fullURL, {
        ...options,
        // Preserve orval's query cancellation signal but fall back to a 30s timeout if not provided
        signal: options.signal ?? AbortSignal.timeout(30_000),
        headers,
    });

    // Counted before the body branches below so every 401 reaches the counter regardless of content type.
    if (res.status === 401 && SessionHandler.loggedIn) {
        SessionHandler.on401();
    }

    const ct = res.headers.get("content-type") ?? "";

    if (res.status === 204) {
        return undefined as T;
    }

    if (ct.includes("application/json")) {
        const text = await res.text();
        const parsed = JSON.parse(text, convertDateStringToDate) as { message: string };

        if (!res.ok) {
            throw new ApiError(res.status, parsed);
        }

        return parsed as T;
    }

    // Non-JSON response — plain text or unexpected content type
    const text = await res.text().catch(() => "");

    if (!res.ok) {
        throw new ApiError(res.status, { message: `${res.statusText}${text ? ` - ${text}` : ""}` });
    }

    return text as unknown as T;
}
